import { Phone, Mail, ArrowUpRight } from "lucide-react";
import { handleCall, handleEmail, GMAIL_COMPOSE_URL } from "../lib/contact";

const ContactHero = () => (
    <section className="pt-24 md:pt-36 pb-12 md:pb-16 px-6 md:px-12" data-testid="contact-hero">
        <div className="max-w-[1600px] mx-auto">
            <p className="text-xs uppercase tracking-[0.4em] text-[#FF1A1A] mb-6">— Direct Line</p>
            <h1 className="font-heading uppercase text-white text-6xl sm:text-8xl md:text-9xl lg:text-[200px] leading-[0.85] tracking-tight">
                Let's
                <br />
                <span className="text-[#FF1A1A]">talk.</span>
            </h1>
            <p className="mt-10 text-zinc-300 text-lg md:text-xl max-w-2xl font-light leading-relaxed">
                No forms. No middlemen. Pick up the phone or send an email — I answer personally.
                Tell me what you want. We'll take it from there.
            </p>
        </div>
    </section>
);

const PhoneCard = () => (
    <a
        href="tel:+14073744236"
        onClick={handleCall}
        className="group relative bg-[#FF1A1A] text-black p-10 md:p-16 block overflow-hidden transition-all duration-500 hover:bg-white min-h-[380px] cursor-pointer"
        data-testid="contact-phone-card"
    >
        <div className="flex items-start justify-between mb-12">
            <div className="w-16 h-16 border-2 border-black flex items-center justify-center">
                <Phone className="w-7 h-7 text-black" />
            </div>
            <ArrowUpRight className="w-10 h-10 text-black group-hover:rotate-45 transition-transform duration-500" />
        </div>
        <div>
            <p className="text-xs uppercase tracking-[0.3em] text-black/70 mb-4">Call Me</p>
            <p className="font-heading uppercase text-black text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight mb-6">
                407
                <br />
                374-4236
            </p>
            <p className="text-black/80 text-sm md:text-base font-light">
                Direct line. Tap to call right now.
            </p>
        </div>
    </a>
);

const EmailCard = () => (
    <a
        href={GMAIL_COMPOSE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleEmail}
        className="group relative bg-[#111111] border border-white/10 text-white p-10 md:p-16 block overflow-hidden transition-all duration-500 hover:border-[#FF1A1A] hover:bg-black min-h-[380px] cursor-pointer"
        data-testid="contact-email-card"
    >
        <div className="flex items-start justify-between mb-12">
            <div className="w-16 h-16 border-2 border-[#FF1A1A] flex items-center justify-center">
                <Mail className="w-7 h-7 text-[#FF1A1A]" />
            </div>
            <ArrowUpRight className="w-10 h-10 text-white group-hover:text-[#FF1A1A] group-hover:rotate-45 transition-all duration-500" />
        </div>
        <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-4">Email Me</p>
            <p className="font-heading uppercase text-white text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-6 break-words">
                tellmeyour
                <br />
                desire666
                <br />
                @gmail.com
            </p>
            <p className="text-zinc-400 text-sm md:text-base font-light">
                Write me. Tell me your desire in full.
            </p>
        </div>
    </a>
);

const ContactBlocks = () => (
    <section className="py-16 md:py-20 px-6 md:px-12" data-testid="contact-blocks">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <PhoneCard />
            <EmailCard />
        </div>
    </section>
);

const ContactInfoStrip = () => (
    <section className="border-y border-white/10 py-12 px-6 md:px-12" data-testid="contact-info-strip">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-3">Who</p>
                <p className="font-heading uppercase text-white text-2xl md:text-3xl leading-none">
                    Elijah <span className="text-[#FF1A1A]">Golden</span>
                </p>
            </div>
            <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-3">What I Offer</p>
                <p className="text-white text-lg leading-relaxed">
                    Funding · Mentorship · Loyalty · Real Results
                </p>
            </div>
            <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-3">When To Call</p>
                <p className="text-white text-lg leading-relaxed">
                    The day you decide your dream is worth acting on.
                </p>
            </div>
        </div>
    </section>
);

const ContactClosing = () => (
    <section className="py-24 md:py-36 px-6 md:px-12" data-testid="contact-closing">
        <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading uppercase text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
                Dreams don't
                <br />
                wait. <span className="text-[#FF1A1A]">Neither</span>
                <br />
                should you.
            </p>
            <div className="mt-14 flex flex-col sm:flex-row gap-4 justify-center">
                <a
                    href="tel:+14073744236"
                    onClick={handleCall}
                    className="group inline-flex items-center justify-center gap-3 bg-[#FF1A1A] text-black font-heading uppercase tracking-widest px-10 py-5 hover:bg-white transition-colors duration-300 cursor-pointer"
                    data-testid="closing-phone-btn"
                >
                    <Phone className="w-5 h-5" />
                    Call 407-374-4236
                </a>
                <a
                    href={GMAIL_COMPOSE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleEmail}
                    className="group inline-flex items-center justify-center gap-3 bg-transparent border-2 border-white text-white font-heading uppercase tracking-widest px-10 py-5 hover:bg-white hover:text-black transition-colors duration-300 cursor-pointer"
                    data-testid="closing-email-btn"
                >
                    <Mail className="w-5 h-5" />
                    Email Elijah
                </a>
            </div>
        </div>
    </section>
);

const Contact = () => (
    <div className="bg-[#050505] text-white min-h-[92vh]" data-testid="contact-page">
        <ContactHero />
        <ContactBlocks />
        <ContactInfoStrip />
        <ContactClosing />
    </div>
);

export default Contact;
